import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useModuleVisibility } from "../../hooks/useModuleVisibility";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Lightning, ChartBar, Target, Cpu } from "@phosphor-icons/react";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import CustomButton from "../../shared/CustomButton/CustomButton";
import Typhography from "../../shared/Typhography/Typhography";
import CustomTable from "../../shared/CustomTable/CustomTable";
import styles from "./CoachSessionPostPage.module.scss";
import { useEndpoints } from "../../hooks/useEndpoints";

type PostTab = "srpe" | "drpe" | "calibration" | "gps";

const TAB_CONFIG: Record<PostTab, { label: string; visibilityKey: string }> = {
  srpe: { label: "sRPE", visibilityKey: "coach-session-post-srpe" },
  drpe: { label: "dRPE", visibilityKey: "coach-session-post-drpe" },
  calibration: { label: "Calibrazione", visibilityKey: "coach-session-post-calibration" },
  gps: { label: "Confronto GPS", visibilityKey: "coach-session-post-gps" },
};

/**
 * Pagina Post-sessione — 4 sotto-tab.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 808-879.
 * Route: /dashboard/teams/:teamId/sessions/:sessionId/post
 */
const CoachSessionPostPage = () => {
  const { sessionId } = useParams<{ sessionId: string; teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const endpoints = useEndpoints();
  const { isTabVisible } = useModuleVisibility();

  // Tab visibili secondo i feature-flag runtime
  const visibleTabs = useMemo(
    () => (Object.keys(TAB_CONFIG) as PostTab[]).filter(
      (t) => isTabVisible(TAB_CONFIG[t].visibilityKey)
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible]
  );

  const [activeTab, setActiveTab] = useState<PostTab>("srpe");

  // Se il tab attivo viene nascosto da un flag, spostati al primo visibile
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.join(','), activeTab]);

  // ─── Fetch session ─────────────────────────────────────────────
  const { data: session, isLoading, error } = useQuery({
    queryKey: ["coachSession", sessionId],
    queryFn: () =>
      endpoints.getCoachSessionById(sessionId!).then((r) => r.data),
    enabled: !!sessionId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["coachSession", sessionId] });

  // ─── sRPE mutation ─────────────────────────────────────────────
  const [srpeValue, setSrpeValue] = useState<string>("");
  const srpeMut = useMutation({
    mutationFn: () =>
      endpoints
        .saveSrpe(sessionId!, { avgAthleteRpe: parseFloat(srpeValue) })
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── dRPE state + mutation ─────────────────────────────────────
  const [drpe, setDrpe] = useState({ respiratory: 5, muscular: 5, mental: 5 });
  const drpeMut = useMutation({
    mutationFn: () =>
      endpoints.saveDrpe(sessionId!, { avgAthleteDRpe: drpe }).then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── Calibration state + mutation ──────────────────────────────
  const [effectiveness, setEffectiveness] = useState<string>("");
  const [calStatus, setCalStatus] = useState<string>("");
  const calibrationMut = useMutation({
    mutationFn: () =>
      endpoints
        .saveCalibration(sessionId!, {
          coachRatingEffectiveness: effectiveness
            ? parseFloat(effectiveness)
            : undefined,
          calibrationStatus: calStatus || undefined,
        })
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── Render ────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.centered}>
          <LeopartLoader size="md" />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.centered}>
          <p className={styles.errorText}>Sessione non trovata</p>
        </div>
      </div>
    );
  }

  const actuals = session.actuals ?? {};
  const metrics = session.sessionMetrics ?? {};

  const getCalibrationBadge = (status?: string) => {
    if (!status) return null;
    if (status === "calibrated")
      return (
        <span className={[styles.statusBadge, styles.badgeCalibrated].join(" ")}>
          Calibrato
        </span>
      );
    if (status === "underestimated")
      return (
        <span className={[styles.statusBadge, styles.badgeUnder].join(" ")}>
          Sottostimato
        </span>
      );
    return (
      <span className={[styles.statusBadge, styles.badgeOver].join(" ")}>
        Sovrastimato
      </span>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          {/* Header */}
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              <button
                className={styles.backBtn}
                onClick={() => navigate(-1)}
                type="button"
              >
                <ArrowLeft size={20} />
              </button>
              <div className={styles.titleBlock}>
                <p className={styles.pageTitle}>Post-sessione</p>
                <p className={styles.pageSubtitle}>
                  {session.title ?? "Sessione"}
                  {session.scheduledDate
                    ? ` · ${new Date(session.scheduledDate).toLocaleDateString("it-IT")}`
                    : ""}
                </p>
              </div>
            </div>
          </div>

          {/* Tab strip — pattern ProgrammingPage */}
          <div className={styles.tabsRow}>
            <div className={styles.tabGroup}>
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={[
                      styles.tabBtn,
                      isActive ? styles.tabBtnActive : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    type="button"
                  >
                    <div className={styles.tabBtnInner}>
                      <p
                        className={[
                          styles.tabLabel,
                          isActive ? styles.tabLabelActive : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {TAB_CONFIG[tab].label}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className={styles.tabContent}>

            {/* ── sRPE ─────────────────────────────────────────── */}
            {activeTab === "srpe" && (
              <div className={styles.srpeSection}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Lightning size={18} color="#ffd15a" />
                  <Typhography size="bold-16">sRPE squadra</Typhography>
                </div>

                {actuals.avgAthleteRpe !== undefined && (
                  <div className={styles.metricRow}>
                    <span className={styles.metricLabel}>Media RPE atleti</span>
                    <span className={styles.metricValue}>
                      {actuals.avgAthleteRpe.toFixed(1)}
                    </span>
                  </div>
                )}

                {metrics.totalEWT !== undefined && actuals.avgAthleteRpe !== undefined && (
                  <div className={styles.metricRow}>
                    <span className={styles.metricLabel}>Carico reale AU</span>
                    <span className={styles.metricValue}>
                      {Math.round(actuals.avgAthleteRpe * (metrics.totalEWT / 60))}
                    </span>
                  </div>
                )}

                <div className={styles.rpeForm}>
                  <Typhography size="bold-14">Inserisci sRPE medio</Typhography>
                  <div className="d-flex align-items-center gap-3">
                    <input
                      type="number"
                      className={styles.rpeInput}
                      placeholder="7.1"
                      min={1}
                      max={10}
                      step={0.1}
                      value={srpeValue}
                      onChange={(e) => setSrpeValue(e.target.value)}
                    />
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Scala 1–10
                    </span>
                  </div>
                  <div className={styles.actions}>
                    <CustomButton
                      color="primary"
                      size="sm"
                      onClick={() => srpeMut.mutate()}
                      disabled={!srpeValue || srpeMut.isPending}
                    >
                      {srpeMut.isPending ? "Salvataggio..." : "Salva sRPE"}
                    </CustomButton>
                  </div>
                </div>
              </div>
            )}

            {/* ── dRPE ─────────────────────────────────────────── */}
            {activeTab === "drpe" && (
              <div className={styles.drpeGrid}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <ChartBar size={18} color="#063444" />
                  <Typhography size="bold-16">dRPE differenziato</Typhography>
                </div>

                {(
                  [
                    { key: "respiratory", label: "RPE respiratorio (cardio / affanno)" },
                    { key: "muscular", label: "RPE muscolare (gambe / spalle)" },
                    { key: "mental", label: "RPE mentale (decisione / attenzione)" },
                  ] as const
                ).map(({ key, label }) => (
                  <div key={key} className={styles.sliderRow}>
                    <div className={styles.sliderLabelRow}>
                      <span className={styles.sliderLabel}>{label}</span>
                      <span className={styles.sliderValue}>{drpe[key]}</span>
                    </div>
                    <input
                      type="range"
                      className={styles.slider}
                      min={1}
                      max={10}
                      step={1}
                      value={drpe[key]}
                      onChange={(e) =>
                        setDrpe((d) => ({
                          ...d,
                          [key]: parseInt(e.target.value, 10),
                        }))
                      }
                    />
                  </div>
                ))}

                <div className={styles.actions}>
                  <CustomButton
                    color="primary"
                    size="sm"
                    onClick={() => drpeMut.mutate()}
                    disabled={drpeMut.isPending}
                  >
                    {drpeMut.isPending ? "Salvataggio..." : "Salva dRPE"}
                  </CustomButton>
                </div>
              </div>
            )}

            {/* ── Calibrazione ──────────────────────────────────── */}
            {activeTab === "calibration" && (
              <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Target size={18} color="#063444" />
                  <Typhography size="bold-16">Calibrazione coach</Typhography>
                </div>

                <CustomTable
                  headers={["Metrica", "Pianificato", "Percepito", "Status"]}
                  data={[
                    {
                      metric: "IPS medio",
                      planned: metrics.avgIPS?.toFixed(1) ?? "—",
                      perceived: "—",
                      status: null,
                    },
                    {
                      metric: "Coach RPE",
                      planned: metrics.coachRpe?.toFixed(1) ?? "—",
                      perceived: actuals.avgAthleteRpe?.toFixed(1) ?? "—",
                      status: getCalibrationBadge(actuals.calibrationStatus),
                    },
                    {
                      metric: "Durata EWT",
                      planned: metrics.totalEWT ? `${Math.round(metrics.totalEWT / 60)} min` : "—",
                      perceived: actuals.actualEWT ? `${Math.round(actuals.actualEWT / 60)} min` : "—",
                      status: null,
                    },
                  ]}
                  fields={{
                    Metrica: { key: "metric" },
                    Pianificato: { key: "planned" },
                    Percepito: { key: "perceived" },
                    Status: {
                      key: "status",
                      customComponent: (value: any) => value ?? "—",
                    },
                  }}
                />

                <div className={styles.effectivenessForm}>
                  <Typhography size="bold-14">Efficacia percepita coach</Typhography>
                  <div className="d-flex align-items-center gap-3">
                    <input
                      type="number"
                      className={styles.rpeInput}
                      placeholder="8"
                      min={1}
                      max={10}
                      value={effectiveness}
                      onChange={(e) => setEffectiveness(e.target.value)}
                    />
                    <select
                      className={styles.rpeInput}
                      style={{ width: "auto", padding: "8px 10px", fontSize: 13 }}
                      value={calStatus}
                      onChange={(e) => setCalStatus(e.target.value)}
                    >
                      <option value="">Auto-detect</option>
                      <option value="calibrated">Calibrato</option>
                      <option value="underestimated">Sottostimato</option>
                      <option value="overestimated">Sovrastimato</option>
                    </select>
                  </div>
                  <div className={styles.actions}>
                    <CustomButton
                      color="primary"
                      size="sm"
                      onClick={() => calibrationMut.mutate()}
                      disabled={calibrationMut.isPending}
                    >
                      {calibrationMut.isPending ? "Salvataggio..." : "Salva calibrazione"}
                    </CustomButton>
                  </div>
                </div>
              </div>
            )}

            {/* ── Confronto GPS ────────────────────────────────── */}
            {activeTab === "gps" && (
              <GpsCompareTab sessionId={sessionId!} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachSessionPostPage;

// ─── GPS compare tab ────────────────────────────────────────────────────────
interface GpsCompareTabProps {
  sessionId: string;
}

const GpsCompareTab = ({ sessionId }: GpsCompareTabProps) => {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["coachSessionGps", sessionId],
    queryFn: () =>
      endpoints.getCoachSessionGpsSummary(sessionId).then((r) => r.data),
  });

  const matchMut = useMutation({
    mutationFn: () => endpoints.matchCoachSessionGps(sessionId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["coachSessionGps", sessionId] }),
  });

  const calibrateMut = useMutation({
    mutationFn: () => endpoints.calibrateCoachSessionIps(sessionId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["coachSessionGps", sessionId] }),
  });

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <LeopartLoader size="sm" />
      </div>
    );
  }

  const d = data as any;
  if (!d?.hasGpsData) {
    return (
      <div className={styles.gpsPlaceholder}>
        <Cpu size={48} color="#e7e7e7" />
        <p className={styles.gpsPlaceholderText}>
          Nessun dato GPS collegato a questa sessione.
        </p>
        <CustomButton
          color="outline"
          size="sm"
          isLoading={matchMut.isPending}
          onClick={() => matchMut.mutate()}
        >
          Prova matching automatico
        </CustomButton>
      </div>
    );
  }

  const s = d.summary ?? {};
  return (
    <div className={styles.gpsCompareBox}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Cpu size={18} color="#063444" />
        <Typhography size="bold-16">Confronto pianificato vs GPS</Typhography>
      </div>

      <div className={styles.kpiGrid}>
        <Kpi label="Distanza totale" value={`${s.totalDistance ?? 0} m`} />
        <Kpi label="HSR" value={`${s.hsrDistance ?? 0} m`} />
        <Kpi label="Sprint" value={s.sprintCount ?? 0} />
        <Kpi label="Accelerazioni" value={s.accelerationCount ?? 0} />
        <Kpi label="Carico GPS (AU)" value={(s.gpsLoadAU ?? 0).toFixed(0)} />
        <Kpi label="IPS equivalente" value={(s.ipsEquivalent ?? 0).toFixed(1)} />
      </div>

      {d.calibrationApplied && d.calibrationDelta != null && (
        <div className={styles.calibInfo}>
          Calibrazione applicata · delta {d.calibrationDelta.toFixed(2)}
        </div>
      )}

      <div className={styles.actions}>
        <CustomButton
          color="outline"
          size="sm"
          isLoading={calibrateMut.isPending}
          onClick={() => calibrateMut.mutate()}
        >
          Calibra pesi IPS
        </CustomButton>
      </div>

      {d.perAthlete && d.perAthlete.length > 0 && (
        <div className={styles.athleteTableWrap}>
          <h4>Dettaglio per atleta</h4>
          <CustomTable
            headers={["Atleta", "Distanza", "HSR", "Sprint", "AU", "IPS perc."]}
            data={d.perAthlete}
            fields={{
              Atleta: { key: "athleteId" },
              Distanza: { key: "distance", customValue: (value: number | undefined) => value?.toFixed(0) ?? "—" },
              HSR: { key: "hsrDistance", customValue: (value: number | undefined) => value?.toFixed(0) ?? "—" },
              Sprint: { key: "sprintCount", customValue: (value: number | undefined) => value ?? "—" },
              AU: { key: "loadAU", customValue: (value: number | undefined) => value?.toFixed(0) ?? "—" },
              "IPS perc.": { key: "ipsPerceived", customValue: (value: number | undefined) => value?.toFixed(1) ?? "—" },
            }}
          />
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value }: { label: string; value: string | number }) => (
  <div className={styles.kpiCell}>
    <span className={styles.kpiLabel}>{label}</span>
    <span className={styles.kpiValue}>{value}</span>
  </div>
);
